// Copyright 2026 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Tour} from "antd";
import * as TourConfig from "../TourConfig";

export const OperationGuideType = {
  Users: "users",
  Expiration: "expiration",
  Games: "games",
  Products: "products",
};

const activeGuideKey = "operationGuide";

const target = selector => () => {
  const element = document.querySelector(selector);
  return element?.getClientRects().length ? element : null;
};

const pageSteps = {
  userList: {
    [OperationGuideType.Users]: [
      {
        title: "确认所属组织",
        description: "添加或删除前，先核对页头当前选择的组织。列表中的操作会直接影响该组织的用户。",
        target: target(".org-select"),
      },
      {
        title: "编辑或删除用户",
        description: "每行右侧可以编辑或删除用户。删除前请核对用户名和组织，并在确认框中再次确认。",
        target: target(".user-list-actions"),
      },
      {
        title: "点击“添加”继续",
        description: "请直接点击高亮的“添加”按钮。系统创建用户记录并进入编辑页后，会自动开始第二段用户信息引导。",
        target: target("#user-add-button"),
        nextButtonProps: {children: "请点击“添加”", disabled: true},
      },
    ],
    [OperationGuideType.Expiration]: [
      {
        title: "选择目标用户",
        description: "找到需要设置有效期的用户，点击右侧“编辑”。直接点击高亮按钮后，引导会在用户编辑页继续。",
        target: target(".user-edit-button"),
        nextButtonProps: {children: "请点击“编辑”", disabled: true},
      },
    ],
  },
  userEdit: {
    [OperationGuideType.Users]: [
      {
        title: "设置用户名",
        description: "确认用户名。用户名是用户的唯一标识，修改前请确认不会影响已有业务。",
        target: target("#user-guide-field-name"),
      },
      {
        title: "设置显示名称",
        description: "填写用户在后台和业务页面中显示的名称。",
        target: target("#user-guide-field-display-name"),
      },
      {
        title: "设置密码",
        description: "为新用户设置可安全交付的初始密码，不要保留系统生成的默认密码。",
        target: target("#user-guide-field-password"),
      },
      {
        title: "设置邮箱",
        description: "填写用户可正常接收邮件的邮箱地址。",
        target: target("#user-guide-field-email"),
      },
      {
        title: "确认注册应用",
        description: "选择用户所属的注册应用，确认该应用与实际业务一致。",
        target: target("#user-guide-field-signup-application"),
      },
      {
        title: "保存用户",
        description: "确认信息后点击“保存 & 退出”。如果不再创建这个用户，请点击“取消”删除未完成的记录。",
        target: target("#user-edit-actions"),
      },
    ],
    [OperationGuideType.Expiration]: [
      {
        title: "设置过期时间",
        description: "选择用户失效的日期和具体时间。到达该时间后用户将无法登录；清空表示不限制有效期。",
        target: target("#user-expire-time-field"),
      },
      {
        title: "保存有效期",
        description: "点击“保存”或“保存 & 退出”使设置生效。请留意浏览器当前时区。",
        target: target("#user-edit-actions"),
      },
    ],
  },
  gameList: {
    [OperationGuideType.Games]: [
      {
        title: "确认所属组织",
        description: "添加游戏前先核对页头当前选择的组织，避免把游戏配置到错误的组织。",
        target: target(".org-select"),
      },
      {
        title: "维护已有游戏",
        description: "点击列表右侧“编辑”可修改已有游戏；删除操作需要在确认框中再次确认。",
        target: target(".game-list-actions"),
      },
      {
        title: "点击“添加”继续",
        description: "请直接点击高亮的“添加”按钮。进入新游戏编辑页后，引导会自动继续。",
        target: target("#game-add-button"),
        nextButtonProps: {children: "请点击“添加”", disabled: true},
      },
    ],
  },
  gameEdit: {
    [OperationGuideType.Games]: [
      {
        title: "确认所属组织",
        description: "确认游戏所属组织，避免把游戏配置到错误的组织。",
        target: target("#game-organization-field"),
      },
      {
        title: "设置内部名称",
        description: "确认游戏内部名称。内部名称用于唯一标识游戏。",
        target: target("#game-name-field"),
      },
      {
        title: "设置展示名称",
        description: "填写面向用户展示的游戏名称。",
        target: target("#game-display-name-field"),
      },
      {
        title: "上传游戏图片",
        description: "上传游戏主图片，并检查图片预览。",
        target: target("#game-image-field"),
      },
      {
        title: "上传 Banner 背景图",
        description: "上传用于 Banner 展示的背景图，并检查图片预览。",
        target: target("#game-bannerBackgroundImage-field"),
      },
      {
        title: "上传 Trending 图标",
        description: "上传 Trending 区域使用的游戏图标，并检查图片预览。",
        target: target("#game-trendingIcon-field"),
      },
      {
        title: "配置视频",
        description: "上传视频或填写视频 URL。启用 Banner 推荐时必须配置视频 URL。",
        target: target("#game-video-field"),
      },
      {
        title: "选择推荐标签",
        description: "在“推荐标签”多选框中选择分类。不要使用下方普通的“标签”字段代替推荐标签。",
        target: target("#game-recommend-tags-field"),
      },
      {
        title: "设置热游指数",
        description: "数值大于 0 时启用热游推荐，数值用于排序优先级。",
        target: target("#game-hot-index-field"),
      },
      {
        title: "设置新游指数",
        description: "数值大于 0 时启用新游推荐，数值用于排序优先级。",
        target: target("#game-new-index-field"),
      },
      {
        title: "设置 Banner 推荐指数",
        description: "数值大于 0 时启用 Banner 推荐。没有视频 URL 时，保存会自动重置为 0。",
        target: target("#game-banner-index-field"),
      },
      {
        title: "填写游戏 URL",
        description: "填写游戏的在线启动地址。",
        target: target("#game-url-field"),
      },
      {
        title: "填写离线包 URL",
        description: "如有离线包，请填写离线包 URL；没有时可留空。",
        target: target("#game-offline-url-field"),
      },
      {
        title: "设置发布状态",
        description: "准备发布时将状态设置为 Published。",
        target: target("#game-state-field"),
      },
      {
        title: "保存配置",
        description: "点击“保存 & 退出”，然后在游戏列表中复核图片、推荐标签、推荐指数和状态。",
        target: target("#game-edit-actions"),
      },
    ],
  },
  productList: {
    [OperationGuideType.Products]: [
      {
        title: "确认所属组织",
        description: "添加产品前先核对页头当前选择的组织，产品和支付渠道都属于该组织。",
        target: target(".org-select"),
      },
      {
        title: "维护和测试产品",
        description: "列表右侧可以购买测试、编辑或删除产品。正式测试前先检查产品状态和支付渠道。",
        target: target(".product-list-actions"),
      },
      {
        title: "点击“添加”继续",
        description: "请直接点击高亮的“添加”按钮。进入产品编辑页后，引导会自动继续。",
        target: target("#product-add-button"),
        nextButtonProps: {children: "请点击“添加”", disabled: true},
      },
    ],
  },
  productEdit: {
    [OperationGuideType.Products]: [
      {
        title: "产品名称代表金币数量",
        description: "重点：在“显示名称”中填写正整数，例如 1000 代表模拟购买 1000 金币。不要把金币数量填到内部“名称”字段。",
        target: target("#product-coin-name-field"),
      },
      {
        title: "选择币种",
        description: "选择模拟支付使用的币种。",
        target: target("#product-currency-field"),
      },
      {
        title: "设置价格",
        description: "填写模拟支付金额。价格不是金币数量。",
        target: target("#product-price-field"),
      },
      {
        title: "设置库存",
        description: "按测试需要设置可购买的库存数量。",
        target: target("#product-quantity-field"),
      },
      {
        title: "支付渠道",
        description: "付款方式请选择 provider_dummy，用于完成模拟付款和金币购买测试。",
        target: target("#product-provider-field"),
      },
      {
        title: "发布产品",
        description: "将状态设置为 Published 后保存；Draft 状态的产品不能购买。",
        target: target("#product-state-field"),
      },
      {
        title: "保存配置",
        description: "点击“保存 & 退出”，回到产品列表后可点击“购买”进行模拟金币购买测试。",
        target: target("#product-edit-actions"),
      },
    ],
  },
};

export function startOperationGuide(type, history, pathname) {
  sessionStorage.setItem(activeGuideKey, type);
  TourConfig.setIsTourVisible(false);
  history.push(pathname);
}

function finishOperationGuide() {
  sessionStorage.removeItem(activeGuideKey);
  sessionStorage.removeItem("operationGuidePreviousTourState");
}

function getCurrentPage() {
  const pathname = window.location.pathname;
  if (pathname === "/users") {
    return "userList";
  } else if (pathname.startsWith("/users/")) {
    return "userEdit";
  } else if (pathname === "/games") {
    return "gameList";
  } else if (pathname.startsWith("/games/")) {
    return "gameEdit";
  } else if (pathname === "/products") {
    return "productList";
  } else if (pathname.startsWith("/products/")) {
    return "productEdit";
  }
  return null;
}

const OperationGuideTour = () => {
  const [open, setOpen] = React.useState(true);
  const [targetReady, setTargetReady] = React.useState(false);
  const [current, setCurrent] = React.useState(0);
  const type = sessionStorage.getItem(activeGuideKey);
  const page = getCurrentPage();
  const steps = page === null ? null : pageSteps[page]?.[type];

  React.useEffect(() => {
    if (!steps) {
      return undefined;
    }

    let attempts = 0;
    let timer;
    const waitForTarget = () => {
      const firstTarget = steps[0]?.target?.();
      if (firstTarget) {
        firstTarget.scrollIntoView({block: "center", inline: "nearest"});
        setTargetReady(true);
      } else if (attempts < 50) {
        attempts += 1;
        timer = window.setTimeout(waitForTarget, 100);
      } else {
        setTargetReady(true);
      }
    };

    waitForTarget();
    return () => window.clearTimeout(timer);
  }, [steps]);

  const closeGuide = () => {
    finishOperationGuide();
    setOpen(false);
  };

  const changeStep = next => {
    const nextTarget = steps[next]?.target?.();
    nextTarget?.scrollIntoView({block: "center", inline: "nearest"});
    setCurrent(next);
  };

  if (!steps || !open || !targetReady) {
    return null;
  }

  return (
    <Tour
      key={`${page}-${current}`}
      open={open}
      current={current}
      steps={steps}
      mask={{color: "rgb(0 0 0 / 55%)"}}
      disabledInteraction={false}
      scrollIntoViewOptions={false}
      onChange={changeStep}
      onClose={closeGuide}
      onFinish={closeGuide}
      indicatorsRender={(current, total) => <span>{current + 1} / {total}</span>}
    />
  );
};

export default OperationGuideTour;
